import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import type { Schedule } from '@tsuyosa/types';
import { useAuth } from '../context/AuthContext';
import { getAdminSchedules, deleteSchedule } from '../lib/api';
import { Spinner } from '@tsuyosa/ui';

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  upcoming:  { label: 'Upcoming',  className: 'text-green-400 bg-green-400/10' },
  sold_out:  { label: 'Sold Out',  className: 'text-red-400 bg-red-400/10' },
  cancelled: { label: 'Cancelled', className: 'text-gray-400 bg-gray-400/10' },
};

export default function SchedulePage() {
  const { accessToken } = useAuth();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!accessToken) return;
    getAdminSchedules(accessToken)
      .then(setSchedules)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load schedules'))
      .finally(() => setIsLoading(false));
  }, [accessToken]);

  async function handleDelete(id: string, title: string) {
    if (!accessToken) return;
    if (!confirm(`Hapus jadwal "${title}"? Tindakan ini tidak dapat dibatalkan.`)) return;
    try {
      await deleteSchedule(accessToken, id);
      setSchedules((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-100">Schedule</h1>
          <p className="mt-0.5 text-sm text-gray-500">{schedules.length} jadwal total</p>
        </div>
        <Link
          to="/schedule/new"
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
          style={{ backgroundColor: 'var(--tsuyosa-pink)' }}
        >
          <Plus size={16} />
          Tambah Jadwal
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      ) : error ? (
        <p className="rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">{error}</p>
      ) : schedules.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center">
          <p className="text-gray-500">Belum ada jadwal. Buat jadwal pertama!</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Tanggal</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Judul</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 hidden md:table-cell">Venue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 hidden lg:table-cell">Tipe</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {schedules.map((s) => {
                const st = STATUS_CONFIG[s.status] ?? STATUS_CONFIG['upcoming']!;
                const typeObj = s.type as { name_id?: string } | undefined;
                return (
                  <tr key={s.id} className="bg-[var(--color-surface)] hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-gray-300 font-mono text-xs whitespace-nowrap">{s.date}</td>
                    <td className="px-4 py-3 text-gray-200 font-medium max-w-[200px] truncate">{s.title}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell">{s.venue ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs hidden lg:table-cell">{typeObj?.name_id ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${st.className}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 justify-end">
                        <Link
                          to={`/schedule/${s.id}/edit`}
                          className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleDelete(s.id, s.title)}
                          className="text-xs text-red-500/60 hover:text-red-400 transition-colors"
                        >
                          Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
